/**
 * "Is this earlier `notes-c` row the SAME patient?" — the one rule behind both
 * prior-visit lookups.
 *
 * Two features search a patient's earlier visits for a document to carry
 * forward, and each used its own half-right key:
 *
 *   - the silent FFP/FFS carry-forward (hooks/medstudent/usePatientDocument.js,
 *     mirrored server-side by backend/api/collab/seed.py) matched on
 *     `patient_name` ONLY, so two different patients sharing a name copied each
 *     other's notes;
 *   - "Copy from older session" (pages/medstudent/MedStudentClinicPage.js)
 *     matched on `EHR_ID` ONLY whenever the row had one — the name was never
 *     checked at all, and a filler ID ("-", "n/a") matched every other
 *     placeholder row in the schema.
 *
 * The shared rule: the NAME always has to match, and when BOTH rows carry a
 * real EHR ID those have to match too. A filler is not a real one.
 *
 * The supabase client is passed in rather than imported, so the rule stays
 * unit-testable and the module stays importable from both the hook and the
 * page layer.
 *
 * Mirrored by `_prior_visit_doc` in backend/api/collab/seed.py — under live
 * collab the SERVER seeds new rooms, so both implementations must agree.
 */

// Phase 4 cutover, site 6 (utils/rdsCutover.js `prior_visit_match`): when
// both switches are on, each key's Supabase query still runs as the parity
// oracle and the fallback, while RDS answers the same question. Off (the
// default, and whenever RDS says the flag is not effective) this is
// byte-for-byte the old behaviour.
import {
  isCutoverEffective,
  startPriorVisitMatchFromRds,
  servePriorVisitMatchFromRds,
} from "./rdsCutover";
// How deep to look. `ROW_LIMIT` is rows pulled per key (cheap, five columns);
// `DOC_LIMIT` is `.json` downloads attempted, since a visit that was scheduled
// but never opened has no document and has to be skipped over.
export const PRIOR_VISIT_ROW_LIMIT = 30;
export const PRIOR_VISIT_DOC_LIMIT = 20;

// ── Names ──────────────────────────────────────────────────────────────────
// Mirrors normalize_name / names_are_same in backend/api/roster_matching.py.
// Every word still has to match — only ORDER is forgiven, because the same
// patient is stored as "Morales, Jose Luis" on one row and "Jose Luis Morales"
// on another. "Jose Luis Torres" is still a different person.

// Apostrophes are ELIDED rather than spaced: "O'Brien" is one word, so
// "o brien" would not match "OBrien".
const ELIDED = /['’`]/g;
// Anything that isn't a letter or digit separates words, so "Smith-Jones",
// "Smith, Jones" and "**Smith Jones" all normalize alike.
const SEPARATORS = /[^a-z0-9]+/g;

export function normalizeName(name) {
  return String(name ?? '')
    .toLowerCase()
    .replace(ELIDED, '')
    .replace(SEPARATORS, ' ')
    .trim();
}

export function namesAreSame(a, b) {
  const left = normalizeName(a);
  const right = normalizeName(b);
  if (!left || !right) return false;
  return left.split(' ').sort().join(' ') === right.split(' ').sort().join(' ');
}

// ── EHR IDs ────────────────────────────────────────────────────────────────

/**
 * An EHR ID only tells two patients apart when it actually identifies someone.
 * Staff type "-", "--", "n/a" or "?" into the field when the real MRN isn't
 * known yet, and comparing those to each other makes every placeholder row in
 * a schema look like one patient. The test is the digit count: a real chart
 * number carries at least this many.
 */
export const MIN_EHR_ID_DIGITS = 3;

export function ehrIdDigits(value) {
  return String(value ?? '').replace(/\D/g, '');
}

export function isUsableEhrId(value) {
  return ehrIdDigits(value).length >= MIN_EHR_ID_DIGITS;
}

/**
 * Comparison form. Case and decoration are noise ("mrn-123 456" is the same
 * chart as "MRN123456"); the characters themselves are not, so "A123456" and
 * "B123456" stay different charts.
 */
export function normalizeEhrId(value) {
  return String(value ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');
}

export function ehrIdsMatch(a, b) {
  return normalizeEhrId(a) === normalizeEhrId(b);
}

// ── The rule ───────────────────────────────────────────────────────────────

/**
 * Is `priorRow` the same patient as `currentRow`, with the reason when not?
 * Returns `{ match, reason }` — the reason is for the console logs these two
 * flows are debugged through.
 */
export function samePatientDecision(currentRow, priorRow) {
  if (!currentRow || !priorRow) return { match: false, reason: 'missing row' };

  if (!namesAreSame(currentRow.patient_name, priorRow.patient_name)) {
    return {
      match: false,
      reason: `name mismatch (${priorRow.patient_name || '—'} vs ${currentRow.patient_name || '—'})`,
    };
  }

  const mine = currentRow.EHR_ID;
  const theirs = priorRow.EHR_ID;
  if (isUsableEhrId(mine) && isUsableEhrId(theirs) && !ehrIdsMatch(mine, theirs)) {
    return { match: false, reason: `EHR ID mismatch (${theirs} vs ${mine})` };
  }

  return { match: true, reason: 'same patient' };
}

export function isSamePatientRow(currentRow, priorRow) {
  return samePatientDecision(currentRow, priorRow).match;
}

/**
 * Reduce raw query rows to the earlier visits worth downloading: same patient,
 * strictly earlier date, de-duplicated (the two key queries overlap), newest
 * first, capped at `limit`.
 */
export function selectPriorVisitRows(currentRow, rows, { limit = PRIOR_VISIT_DOC_LIMIT, onReject } = {}) {
  const currentDate = currentRow?.date_ ? String(currentRow.date_) : null;
  const seen = new Set();
  const kept = [];

  for (const row of rows || []) {
    if (!row || !row.date_) continue;
    if (currentDate && String(row.date_) >= currentDate) continue;

    const key = row.id != null
      ? `id:${row.id}`
      : `k:${row.patient_name}|${row.date_}|${row.EHR_ID}`;
    if (seen.has(key)) continue;
    seen.add(key);

    const decision = samePatientDecision(currentRow, row);
    if (!decision.match) {
      if (onReject) onReject(row, decision.reason);
      continue;
    }
    kept.push(row);
  }

  kept.sort((a, b) => String(b.date_).localeCompare(String(a.date_)));
  return limit > 0 ? kept.slice(0, limit) : kept;
}

/** "Doe, Jane · EHR 123456" — who a carried-over document is actually from. */
export function formatPatientLabel(row) {
  const name = String(row?.patient_name || '').trim() || 'Unknown patient';
  const ehr = String(row?.EHR_ID ?? '').trim();
  return ehr ? `${name} · EHR ${ehr}` : name;
}

// ── The query ──────────────────────────────────────────────────────────────

/**
 * Which columns to search on. Name is always one of them. A usable EHR ID adds
 * a second query rather than replacing the first, because neither key alone
 * returns every row of one patient:
 *
 *   - `.eq('patient_name', …)` is byte-exact in Postgres, so it misses the
 *     rows stored as "Jose Morales" when this visit says "Morales, Jose" —
 *     rows the EHR query finds and `namesAreSame` then accepts;
 *   - the EHR query misses this patient's OWN earlier rows that were filed
 *     before the MRN was known and still hold a filler.
 *
 * The union is a superset of what either lookup used to see; `samePatientDecision`
 * is what actually decides.
 */
export function priorVisitQueryKeys(patientRow) {
  const keys = [];
  const name = String(patientRow?.patient_name || '').trim();
  if (name) keys.push({ column: 'patient_name', value: patientRow.patient_name });
  if (isUsableEhrId(patientRow?.EHR_ID)) {
    keys.push({ column: 'EHR_ID', value: String(patientRow.EHR_ID).trim() });
  }
  return keys;
}

/**
 * Fetch this patient's earlier visits, newest first.
 *
 * Returns `{ candidates, error, keys }`. `candidates` is already filtered by
 * the same-patient rule and ready to walk; callers download each one's `.json`
 * in order and take the first populated document.
 */
export async function fetchPriorVisitCandidates(supabase, patientRow, schema, { log } = {}) {
  const before = patientRow?.date_;
  if (!before) return { candidates: [], rows: [], keys: [], error: null };

  const keys = priorVisitQueryKeys(patientRow);
  if (keys.length === 0) return { candidates: [], rows: [], keys: [], error: null };

  // Decided once per call (not per key): the flag is cached client-side
  // and two keys share the same fetch. Off = identical to today.
  const cutoverOn = await isCutoverEffective("prior_visit_match");

  const results = await Promise.all(keys.map(async ({ column, value }) => {
    // Started BEFORE the Supabase read so the two round trips overlap.
    // Skip the RDS call entirely when the cutover is off -- there is no
    // oracle to compare against, so /query is not consulted.
    const rdsStarted = cutoverOn
      ? startPriorVisitMatchFromRds(schema || 'public', {
          key: { column, value },
          before,
          signal: null,
        })
      : null;
    let rows;
    try {
      const { data, error } = await supabase
        .schema(schema || 'public')
        .from('notes-c')
        .select('id, EHR_ID, patient_name, date_, type')
        .eq(column, value)
        .lt('date_', before)
        .order('date_', { ascending: false })
        .limit(PRIOR_VISIT_ROW_LIMIT);
      // A failed Supabase key costs only its own candidates; the rest of
      // the union stands. With no oracle RDS is not consulted either.
      if (error) return { column, rows: [], error };
      rows = Array.isArray(data) ? data : [];
      if (rdsStarted) {
        // eslint-disable-next-line no-await-in-loop
        const served = await servePriorVisitMatchFromRds(schema || 'public', {
          key: { column, value },
          before,
          supabaseRows: rows,
          started: rdsStarted,
        });
        rows = served.rows;
      }
      return { column, rows, error: null };
    } catch (_) {
      return { column, rows: [], error: null };
    }
  }));

  const rows = results.flatMap((r) => r.rows);
  const error = results.find((r) => r.error)?.error || null;
  const rejected = [];
  const candidates = selectPriorVisitRows(patientRow, rows, {
    onReject: (row, reason) => rejected.push(`${row.date_}: ${reason}`),
  });

  if (log) {
    log({
      schema: schema || 'public',
      searchedBy: keys.map((k) => `${k.column}=${k.value}`),
      before,
      error: error?.message,
      fetched: rows.length,
      kept: candidates.map((r) => `${r.date_}${r.type ? ` (${r.type})` : ''}`),
      rejected,
    });
  }

  return { candidates, rows, keys, error };
}
